import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Shield, Building2, MessageSquare, Trash2, Users, LogOut, Loader2, Save, RotateCcw, AlertTriangle, CreditCard, Tag, ChevronRight, Pencil, Plus, X, Eye, BarChart3, Settings, Play, Mail, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminEmailPanel from '../components/admin/AdminEmailPanel';

const API_BASE = '/api';

function adminApi() {
  const token = localStorage.getItem('admin_token');
  return axios.create({
    baseURL: API_BASE,
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ──────────────────────────────────
// COMPANY DETAIL PANEL
// ──────────────────────────────────
function CompanyDetailPanel({ companyId, onClose, plans, onCompanyUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limitInput, setLimitInput] = useState('');
  const [limitSaving, setLimitSaving] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [demoSaving, setDemoSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi().get(`/admin/companies/${companyId}/detail`);
        setData(res.data);
        setLimitInput(res.data.company.lead_limit_override ?? '');
      } catch { toast.error('Details konnten nicht geladen werden'); }
      setLoading(false);
    })();
  }, [companyId]);

  const saveLimitOverride = async () => {
    setLimitSaving(true);
    try {
      await adminApi().put(`/admin/companies/${companyId}/lead-limit`, {
        lead_limit_override: limitInput === '' ? null : parseInt(limitInput)
      });
      toast.success('Lead-Limit gespeichert');
      const res = await adminApi().get(`/admin/companies/${companyId}/detail`);
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    setLimitSaving(false);
  };

  const changePlan = async (planId) => {
    setPlanSaving(true);
    try {
      await adminApi().put(`/admin/companies/${companyId}/plan`, { plan_id: planId });
      toast.success('Plan aktualisiert');
      const res = await adminApi().get(`/admin/companies/${companyId}/detail`);
      setData(res.data);
      onCompanyUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    setPlanSaving(false);
  };

  const toggleDemo = async () => {
    setDemoSaving(true);
    try {
      const newValue = !data.company.is_demo;
      await adminApi().put(`/admin/companies/${companyId}/demo`, { is_demo: newValue });
      toast.success(newValue ? 'Demo-Modus aktiviert' : 'Demo-Modus deaktiviert');
      const res = await adminApi().get(`/admin/companies/${companyId}/detail`);
      setData(res.data);
      onCompanyUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    setDemoSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>;
  if (!data) return null;

  const { company, users, usage, plan, total_leads_all_time, recent_runs } = data;
  const usagePercent = usage.effective_limit > 0 ? Math.min(100, Math.round(usage.leads_used_this_month / usage.effective_limit * 100)) : 0;

  return (
    <div className="space-y-5" data-testid="company-detail-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">{company.name}</h3>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Nutzer', value: users.length, max: company.max_users },
          { label: 'Leads (Monat)', value: usage.leads_used_this_month, max: usage.effective_limit },
          { label: 'Leads (Gesamt)', value: total_leads_all_time },
          { label: 'Suchläufe', value: recent_runs.length },
        ].map((s, i) => (
          <div key={i} className="bg-slate-800 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-white">{s.value}{s.max !== undefined ? <span className="text-sm text-slate-400">/{s.max}</span> : null}</div>
            <div className="text-xs text-slate-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Usage bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-slate-400">
          <span>Lead-Verbrauch {usage.month}</span>
          <span>{usagePercent}%</span>
        </div>
        <div className="w-full h-2 rounded-full bg-slate-700 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${usagePercent}%` }} />
        </div>
      </div>

      {/* Demo Toggle */}
      <div className="flex items-center justify-between bg-slate-800 rounded-lg px-4 py-3">
        <div>
          <div className="text-sm font-medium text-white flex items-center gap-2">
            <Play className="w-4 h-4 text-cyan-400" />
            Demo-Modus
          </div>
          <p className="text-xs text-slate-400 mt-0.5">Unbegrenzte Leads, keine Zahlung erforderlich</p>
        </div>
        <div className="flex items-center gap-2">
          {demoSaving && <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />}
          <Switch
            checked={company.is_demo || false}
            onCheckedChange={toggleDemo}
            disabled={demoSaving}
            data-testid="admin-demo-toggle"
          />
        </div>
      </div>

      {/* Plan + Lead Limit Override */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-400 text-xs">Abo-Plan</Label>
          <Select value={company.subscription_plan || 'basic'} onValueChange={changePlan} disabled={planSaving}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="admin-plan-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {plans.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name} ({p.max_leads_per_month} Leads)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-400 text-xs">Lead-Limit Override</Label>
          <div className="flex gap-2">
            <Input type="number" placeholder={`Standard: ${plan.max_leads_per_month}`}
              value={limitInput} onChange={e => setLimitInput(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white" data-testid="admin-lead-limit-input" />
            <Button size="sm" onClick={saveLimitOverride} disabled={limitSaving}
              className="bg-amber-600 hover:bg-amber-700" data-testid="admin-save-limit-btn">
              {limitSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-slate-500">Leer = Plan-Standard ({plan.max_leads_per_month})</p>
        </div>
      </div>

      {/* Users table */}
      <div>
        <h4 className="text-sm font-semibold text-slate-300 mb-2">Nutzer ({users.length})</h4>
        <div className="rounded-lg border border-slate-700 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400 text-xs">E-Mail</TableHead>
                <TableHead className="text-slate-400 text-xs">Rolle</TableHead>
                <TableHead className="text-slate-400 text-xs text-right">Leads (Monat)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} className="border-slate-700">
                  <TableCell className="text-white text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${u.role === 'owner' ? 'text-amber-400 border-amber-600' : 'text-slate-400'}`}>{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm text-slate-300">{usage.per_user[u.id] || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Recent runs */}
      {recent_runs.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-300 mb-2">Letzte Suchläufe</h4>
          <div className="space-y-1">
            {recent_runs.slice(0, 5).map(r => (
              <div key={r.id} className="flex justify-between items-center text-xs bg-slate-800 rounded px-3 py-1.5">
                <span className="text-slate-300">{new Date(r.created_at).toLocaleDateString('de-DE')} {r.filters_region && `— ${r.filters_region}`} {r.filters_industry && `(${r.filters_industry})`}</span>
                <Badge variant="outline" className={`text-xs ${r.status === 'completed' ? 'text-emerald-400' : r.status === 'failed' ? 'text-red-400' : 'text-amber-400'}`}>
                  {r.accounts_found} Leads
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing/Invoice Data */}
      {company.billing_data && (
        <div data-testid="admin-billing-data">
          <h4 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Rechnungsdaten
          </h4>
          <div className="rounded-lg border border-slate-700 bg-slate-800 p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Firma:</span><span className="text-white">{company.billing_data.company_name}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Ansprechpartner:</span><span className="text-white">{company.billing_data.contact_person}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">E-Mail:</span><span className="text-white">{company.billing_data.email}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Adresse:</span><span className="text-white">{company.billing_data.street}, {company.billing_data.zip} {company.billing_data.city}</span></div>
            {company.billing_data.ust_id && <div className="flex justify-between"><span className="text-slate-400">USt-IdNr.:</span><span className="text-white">{company.billing_data.ust_id}</span></div>}
          </div>
          {company.invoice_order && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <Badge className={`${company.invoice_order.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                {company.invoice_order.status === 'pending' ? 'Rechnung ausstehend' : 'Bezahlt'}
              </Badge>
              <span className="text-slate-400">Plan: {company.invoice_order.plan_name} ({company.invoice_order.price_eur}€/Mo)</span>
              <span className="text-slate-500">— {new Date(company.invoice_order.ordered_at).toLocaleDateString('de-DE')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────
// MAIN ADMIN DASHBOARD
// ──────────────────────────────────
export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [prompts, setPrompts] = useState({});
  const [originalPrompts, setOriginalPrompts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [plansSaving, setPlansSaving] = useState(false);
  const [editPlans, setEditPlans] = useState([]);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({ code: '', type: 'all', plan_ids: [], discount_percent: 10, max_uses: '', valid_until: '', active: true });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Generate month options (last 12 months)
  const monthOptions = React.useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
      opts.push({ value: val, label });
    }
    return opts;
  }, []);

  const loadCompanies = useCallback(async (month) => {
    try {
      const res = await adminApi().get(`/admin/companies?month=${month}`);
      setCompanies(res.data.companies || []);
    } catch {}
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [compRes, promptRes, plansRes, couponsRes] = await Promise.all([
        adminApi().get(`/admin/companies?month=${selectedMonth}`),
        adminApi().get('/admin/prompts'),
        adminApi().get('/admin/plans'),
        adminApi().get('/admin/coupons'),
      ]);
      setCompanies(compRes.data.companies || []);
      setPrompts(promptRes.data.prompts || {});
      setOriginalPrompts(promptRes.data.prompts || {});
      const loadedPlans = plansRes.data.plans || [];
      setPlans(loadedPlans);
      setEditPlans(JSON.parse(JSON.stringify(loadedPlans)));
      setCoupons(couponsRes.data.coupons || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedMonth]);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/admin/login'); return; }
    loadData();
  }, [loadData, navigate]);

  // Reload companies when month changes
  const handleMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
    loadCompanies(newMonth);
  };

  const logout = () => { localStorage.removeItem('admin_token'); navigate('/admin/login'); };

  const deleteCompany = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await adminApi().delete(`/admin/companies/${deleteTarget.id}`);
      setCompanies(c => c.filter(x => x.id !== deleteTarget.id));
      toast.success(`"${deleteTarget.name}" gelöscht`);
      setDeleteTarget(null);
      setSelectedCompanyId(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen'); }
    setDeleteLoading(false);
  };

  const savePrompts = async () => {
    setSaving(true);
    try {
      await adminApi().put('/admin/prompts', prompts);
      setOriginalPrompts({ ...prompts });
      toast.success('System-Prompts gespeichert');
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  const resetPrompts = () => { setPrompts({ ...originalPrompts }); toast.info('Änderungen verworfen'); };
  const hasPromptChanges = JSON.stringify(prompts) !== JSON.stringify(originalPrompts);
  const hasPlansChanges = JSON.stringify(editPlans) !== JSON.stringify(plans);

  const savePlans = async () => {
    setPlansSaving(true);
    try {
      await adminApi().put('/admin/plans', { plans: editPlans });
      toast.success('Abo-Pläne gespeichert');
      const res = await adminApi().get('/admin/plans');
      const loaded = res.data.plans || [];
      setPlans(loaded);
      setEditPlans(JSON.parse(JSON.stringify(loaded)));
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setPlansSaving(false);
  };

  const updatePlanField = (idx, field, value) => {
    setEditPlans(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const openCouponForm = (coupon = null) => {
    if (coupon) {
      setEditCoupon(coupon);
      setCouponForm({ code: coupon.code, type: coupon.type, plan_ids: coupon.plan_ids || [], discount_percent: coupon.discount_percent, max_uses: coupon.max_uses || '', valid_until: coupon.valid_until || '', active: coupon.active });
    } else {
      setEditCoupon(null);
      setCouponForm({ code: '', type: 'all', plan_ids: [], discount_percent: 10, max_uses: '', valid_until: '', active: true });
    }
    setShowCouponDialog(true);
  };

  const saveCoupon = async () => {
    try {
      if (editCoupon) {
        await adminApi().put(`/admin/coupons/${editCoupon.id}`, couponForm);
        toast.success('Gutschein aktualisiert');
      } else {
        await adminApi().post('/admin/coupons', couponForm);
        toast.success('Gutschein erstellt');
      }
      setShowCouponDialog(false);
      const res = await adminApi().get('/admin/coupons');
      setCoupons(res.data.coupons || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteCoupon = async (id) => {
    try {
      await adminApi().delete(`/admin/coupons/${id}`);
      setCoupons(c => c.filter(x => x.id !== id));
      toast.success('Gutschein gelöscht');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
  const daysSince = (iso) => {
    if (!iso) return '—';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return days === 0 ? 'Heute' : days === 1 ? '1 Tag' : `${days} Tage`;
  };
  const planBadge = (plan, status) => {
    if (!plan || plan === 'Kein Abo') return <Badge variant="outline" className="text-xs">Kein Abo</Badge>;
    const colors = { active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', past_due: 'bg-amber-100 text-amber-700', canceled: 'bg-red-100 text-red-700' };
    return <Badge variant="outline" className={`text-xs ${colors[status] || ''}`}>{plan}{status === 'active' ? '' : ` (${status})`}</Badge>;
  };

  const promptConfig = [
    { key: 'company_analysis', label: 'Unternehmensanalyse', desc: 'System-Prompt für die erstmalige Analyse der Unternehmenswebsite.' },
    { key: 'customer_discovery', label: 'Kundensuche', desc: 'System-Prompt für die KI-gestützte Suche nach potenziellen B2B-Kunden.' },
    { key: 'icp_generation', label: 'Zielgruppen-Definition (ICP)', desc: 'System-Prompt für die Generierung des Ideal Customer Profiles.' },
  ];

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-dashboard">
      <header className="border-b border-slate-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h1 className="text-lg font-bold">LaizyLeads Admin</h1>
              <p className="text-xs text-slate-400">Verwaltungsoberfläche</p>
            </div>
          </div>
          <Button variant="ghost" className="text-slate-400 hover:text-white" onClick={logout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />Abmelden
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="companies" className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="companies" data-testid="tab-companies" className="data-[state=active]:bg-slate-800">
              <Building2 className="w-4 h-4 mr-2" />Unternehmen
            </TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans" className="data-[state=active]:bg-slate-800">
              <CreditCard className="w-4 h-4 mr-2" />Abo-Verwaltung
            </TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons" className="data-[state=active]:bg-slate-800">
              <Tag className="w-4 h-4 mr-2" />Gutscheine
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails" className="data-[state=active]:bg-slate-800">
              <Mail className="w-4 h-4 mr-2" />E-Mail
            </TabsTrigger>
            <TabsTrigger value="prompts" data-testid="tab-prompts" className="data-[state=active]:bg-slate-800">
              <MessageSquare className="w-4 h-4 mr-2" />System-Prompts
            </TabsTrigger>
          </TabsList>

          {/* ═══ TAB 1: COMPANIES ═══ */}
          <TabsContent value="companies">
            <div className="space-y-4">
              {/* Month filter + Stats */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-bold text-white">Unternehmen ({companies.length})</h2>
                  <Select value={selectedMonth} onValueChange={handleMonthChange}>
                    <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700 text-white" data-testid="month-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-slate-400">Gesamt-Leads:
                    <span className="text-white font-semibold ml-1">{companies.reduce((s, c) => s + (c.leads_this_month || 0), 0)}</span>
                  </span>
                </div>
              </div>

              {/* Companies Table or Detail split */}
              <div className={`grid gap-6 ${selectedCompanyId ? 'grid-cols-1 xl:grid-cols-12' : 'grid-cols-1'}`}>
                <Card className={`bg-slate-900 border-slate-800 overflow-hidden ${selectedCompanyId ? 'xl:col-span-5' : ''}`}>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table className="table-fixed w-full">
                        <TableHeader>
                          <TableRow className="border-slate-800 hover:bg-transparent">
                            <TableHead className="text-slate-400 text-xs whitespace-nowrap w-[180px] min-w-[140px]">Unternehmen</TableHead>
                            <TableHead className="text-slate-400 text-xs whitespace-nowrap w-[100px]">Abo</TableHead>
                            <TableHead className="text-slate-400 text-xs whitespace-nowrap text-center w-[70px]">Nutzer</TableHead>
                            <TableHead className="text-slate-400 text-xs whitespace-nowrap text-center w-[70px]">Leads</TableHead>
                            {!selectedCompanyId && <>
                              <TableHead className="text-slate-400 text-xs whitespace-nowrap w-[220px]">Branche</TableHead>
                              <TableHead className="text-slate-400 text-xs whitespace-nowrap w-[100px]">Erste Zahlung</TableHead>
                              <TableHead className="text-slate-400 text-xs whitespace-nowrap w-[100px]">Nächste Zahlung</TableHead>
                              <TableHead className="text-slate-400 text-xs whitespace-nowrap w-[100px]">Registriert</TableHead>
                            </>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {companies.map(c => {
                            const leadsPercent = c.max_leads > 0 ? Math.round(c.leads_this_month / c.max_leads * 100) : 0;
                            const isSelected = selectedCompanyId === c.id;
                            return (
                              <TableRow key={c.id}
                                className={`border-slate-800 cursor-pointer transition-colors ${isSelected ? 'bg-slate-800' : 'hover:bg-slate-800/40'}`}
                                onClick={() => setSelectedCompanyId(isSelected ? null : c.id)}
                                data-testid={`company-row-${c.id}`}>
                                <TableCell className="py-3">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="font-medium text-white text-sm leading-tight truncate" title={c.name}>{c.name}</div>
                                    {c.is_demo && <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 text-[10px] px-1.5 py-0 shrink-0">Demo</Badge>}
                                  </div>
                                  {selectedCompanyId && c.industry && c.industry !== '—' && (
                                    <div className="text-xs text-slate-500 mt-0.5 truncate" title={c.industry}>{c.industry}</div>
                                  )}
                                </TableCell>
                                <TableCell className="whitespace-nowrap">
                                  {planBadge(c.subscription_plan, c.subscription_status)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-white font-medium">{c.user_count}</span>
                                  <span className="text-slate-500 text-xs">/{c.max_users}</span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="flex items-center gap-1.5 justify-center">
                                    <span className={`font-medium ${!c.is_demo && leadsPercent > 90 ? 'text-red-400' : !c.is_demo && leadsPercent > 70 ? 'text-amber-400' : 'text-white'}`}>
                                      {c.leads_this_month}
                                    </span>
                                    <span className="text-slate-500 text-xs">/{c.is_demo ? '∞' : c.max_leads}</span>
                                  </div>
                                </TableCell>
                                {!selectedCompanyId && <>
                                  <TableCell className="text-slate-400 text-sm">
                                    <span className="block truncate" title={c.industry}>{c.industry}</span>
                                  </TableCell>
                                  <TableCell className="text-slate-400 text-sm whitespace-nowrap">{c.first_payment ? formatDate(c.first_payment) : '—'}</TableCell>
                                  <TableCell className="text-slate-400 text-sm whitespace-nowrap">{c.next_payment ? formatDate(c.next_payment) : '—'}</TableCell>
                                  <TableCell className="text-slate-400 text-sm whitespace-nowrap">{formatDate(c.created_at)}</TableCell>
                                </>}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* Detail panel */}
                {selectedCompanyId && (
                  <Card className="bg-slate-900 border-slate-800 xl:col-span-7">
                    <CardContent className="pt-6">
                      <CompanyDetailPanel
                        companyId={selectedCompanyId}
                        onClose={() => setSelectedCompanyId(null)}
                        plans={plans}
                        onCompanyUpdate={() => loadCompanies(selectedMonth)}
                      />
                      <div className="mt-4 pt-4 border-t border-slate-700">
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300"
                          onClick={() => setDeleteTarget(companies.find(c => c.id === selectedCompanyId))}
                          data-testid="delete-selected-company-btn">
                          <Trash2 className="w-4 h-4 mr-2" /> Unternehmen löschen
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ═══ TAB 2: PLAN MANAGEMENT ═══ */}
          <TabsContent value="plans">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Abo-Pläne verwalten</CardTitle>
                    <CardDescription className="text-slate-400">Preise, Lead-Limits, Badge-Texte und Einmalzahlungen konfigurieren</CardDescription>
                  </div>
                  <Button className="bg-amber-600 hover:bg-amber-700" onClick={savePlans} disabled={plansSaving || !hasPlansChanges} data-testid="save-plans-btn">
                    {plansSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {editPlans.map((plan, idx) => (
                    <div key={plan.id} className="rounded-lg border border-slate-700 p-5 space-y-4" data-testid={`plan-edit-${plan.id}`}>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-sm">{plan.id.toUpperCase()}</Badge>
                        <Input value={plan.name} onChange={e => updatePlanField(idx, 'name', e.target.value)}
                          className="bg-slate-800 border-slate-700 text-white max-w-[200px]" placeholder="Plan-Name" />
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">Monatspreis (EUR)</Label>
                          <Input type="number" step="0.01" value={plan.monthly_eur} onChange={e => updatePlanField(idx, 'monthly_eur', parseFloat(e.target.value) || 0)}
                            className="bg-slate-800 border-slate-700 text-white" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">Jahrespreis (EUR)</Label>
                          <Input type="number" step="0.01" value={plan.yearly_eur} onChange={e => updatePlanField(idx, 'yearly_eur', parseFloat(e.target.value) || 0)}
                            className="bg-slate-800 border-slate-700 text-white" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">Max. Nutzer</Label>
                          <Input type="number" value={plan.max_users} onChange={e => updatePlanField(idx, 'max_users', parseInt(e.target.value) || 1)}
                            className="bg-slate-800 border-slate-700 text-white" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">Leads / Monat</Label>
                          <Input type="number" value={plan.max_leads_per_month} onChange={e => updatePlanField(idx, 'max_leads_per_month', parseInt(e.target.value) || 0)}
                            className="bg-slate-800 border-slate-700 text-white" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">Badge-Text</Label>
                          <Input value={plan.badge_text || ''} onChange={e => updatePlanField(idx, 'badge_text', e.target.value)}
                            className="bg-slate-800 border-slate-700 text-white" placeholder="z.B. Beliebtester Plan" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">Addon-Leads (Einmalzahlung)</Label>
                          <Input type="number" value={plan.addon_leads || 100} onChange={e => updatePlanField(idx, 'addon_leads', parseInt(e.target.value) || 100)}
                            className="bg-slate-800 border-slate-700 text-white" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-slate-400 text-xs">Addon-Preis (EUR)</Label>
                          <Input type="number" step="0.01" value={plan.addon_price_eur || 29.99} onChange={e => updatePlanField(idx, 'addon_price_eur', parseFloat(e.target.value) || 0)}
                            className="bg-slate-800 border-slate-700 text-white" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ TAB 3: COUPONS ═══ */}
          <TabsContent value="coupons">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Gutschein-Codes</CardTitle>
                    <CardDescription className="text-slate-400">Rabattcodes für Abonnements und Einmalzahlungen verwalten</CardDescription>
                  </div>
                  <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => openCouponForm()} data-testid="add-coupon-btn">
                    <Plus className="w-4 h-4 mr-2" /> Neuer Gutschein
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {coupons.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Tag className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>Noch keine Gutschein-Codes angelegt</p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-800 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-800 hover:bg-transparent">
                          <TableHead className="text-slate-400">Code</TableHead>
                          <TableHead className="text-slate-400">Typ</TableHead>
                          <TableHead className="text-slate-400">Rabatt</TableHead>
                          <TableHead className="text-slate-400">Nutzungen</TableHead>
                          <TableHead className="text-slate-400">Gültig bis</TableHead>
                          <TableHead className="text-slate-400">Status</TableHead>
                          <TableHead className="text-slate-400 text-right">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {coupons.map(c => (
                          <TableRow key={c.id} className="border-slate-800" data-testid={`coupon-row-${c.id}`}>
                            <TableCell className="font-mono font-bold text-white">{c.code}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{c.type === 'subscription' ? 'Abo' : c.type === 'addon' ? 'Einmalzahlung' : 'Alle'}</Badge>
                            </TableCell>
                            <TableCell className="text-emerald-400 font-semibold">{c.discount_percent}%</TableCell>
                            <TableCell className="text-slate-300">{c.current_uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ' (unbegrenzt)'}</TableCell>
                            <TableCell className="text-slate-400 text-sm">{c.valid_until ? formatDate(c.valid_until) : 'Unbegrenzt'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-xs ${c.active ? 'text-emerald-400 border-emerald-600' : 'text-red-400 border-red-600'}`}>
                                {c.active ? 'Aktiv' : 'Inaktiv'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={() => openCouponForm(c)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => deleteCoupon(c.id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ TAB: E-MAIL ═══ */}
          <TabsContent value="emails">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <AdminEmailPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ TAB 4: SYSTEM PROMPTS ═══ */}
          <TabsContent value="prompts">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">System-Prompts</h2>
                  <p className="text-sm text-slate-400 mt-1">KI-Anweisungen für die verschiedenen Analyseschritte</p>
                </div>
                <div className="flex items-center gap-2">
                  {hasPromptChanges && (
                    <Button variant="outline" className="border-slate-700 text-slate-300" onClick={resetPrompts}><RotateCcw className="w-4 h-4 mr-2" />Verwerfen</Button>
                  )}
                  <Button className="bg-amber-600 hover:bg-amber-700" onClick={savePrompts} disabled={saving || !hasPromptChanges} data-testid="save-prompts-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Speichern
                  </Button>
                </div>
              </div>
              {promptConfig.map(({ key, label, desc }) => (
                <Card key={key} className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-base">{label}</CardTitle>
                    <CardDescription className="text-slate-400">{desc}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea value={prompts[key] || ''} onChange={e => setPrompts(p => ({ ...p, [key]: e.target.value }))}
                      className="min-h-[200px] bg-slate-800 border-slate-700 text-slate-200 font-mono text-sm leading-relaxed resize-y" placeholder="System-Prompt eingeben..." data-testid={`prompt-${key}`} />
                    <p className="text-xs text-slate-500 mt-2">{(prompts[key] || '').length} Zeichen</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Unternehmen löschen</DialogTitle>
            <DialogDescription className="text-slate-400">
              <span className="block mt-2">Sind Sie sicher, dass Sie <strong className="text-white">{deleteTarget?.name}</strong> löschen möchten?</span>
              <span className="block mt-2 text-red-400 font-medium">Alle Daten (Nutzer, Leads, Suchläufe) werden unwiderruflich gelöscht.</span>
              <span className="block mt-1 text-slate-400 text-sm">Falls ein Stripe-Abo existiert, wird dieses automatisch gekündigt.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="border-slate-700" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={deleteCompany} disabled={deleteLoading} data-testid="confirm-delete-company-btn">
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon Create/Edit Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="coupon-dialog">
          <DialogHeader>
            <DialogTitle>{editCoupon ? 'Gutschein bearbeiten' : 'Neuer Gutschein'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Code</Label>
                <Input value={couponForm.code} onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  className="bg-slate-800 border-slate-700 text-white font-mono" placeholder="z.B. WELCOME20" data-testid="coupon-code-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Typ</Label>
                <Select value={couponForm.type} onValueChange={v => setCouponForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle (Abo + Einmalzahlung)</SelectItem>
                    <SelectItem value="subscription">Nur Abonnement</SelectItem>
                    <SelectItem value="addon">Nur Einmalzahlung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Rabatt (%)</Label>
                <Input type="number" value={couponForm.discount_percent} onChange={e => setCouponForm(f => ({ ...f, discount_percent: parseFloat(e.target.value) || 0 }))}
                  className="bg-slate-800 border-slate-700 text-white" data-testid="coupon-discount-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Max. Nutzungen</Label>
                <Input type="number" value={couponForm.max_uses} onChange={e => setCouponForm(f => ({ ...f, max_uses: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white" placeholder="Unbegrenzt" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Gültig bis</Label>
                <Input type="date" value={couponForm.valid_until} onChange={e => setCouponForm(f => ({ ...f, valid_until: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={couponForm.active} onCheckedChange={v => setCouponForm(f => ({ ...f, active: v }))} />
              <Label className="text-slate-300">Aktiv</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-700" onClick={() => setShowCouponDialog(false)}>Abbrechen</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={saveCoupon} data-testid="save-coupon-btn">
              <Save className="w-4 h-4 mr-2" />{editCoupon ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
